import { useState, useEffect } from "react";
import { Fuel, Loader2, Save, Lock, Unlock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "base_price", label: "Prezzo gasolio di riferimento (€/L)", step: "0.001", hint: "Prezzo su cui hai costruito il listino" },
  { key: "current_price", label: "Prezzo gasolio attuale (€/L)", step: "0.001", hint: "Aggiornalo ogni mese (media MIMIT)" },
  { key: "incidence_pct", label: "Incidenza carburante sul costo (%)", step: "0.5", hint: "Di solito 25–35%" },
  { key: "step", label: "Ampiezza scaglione (€/L)", step: "0.01", hint: "es. 0.05 = uno scaglione ogni 5 cent" },
  { key: "max_tiers", label: "Numero scaglioni", step: "1", hint: "Righe della tabella sopra il prezzo base" },
];

export default function FuelSurchargeSection() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const token = localStorage.getItem("mv_admin_token");
  const h = { headers: { Authorization: `Bearer ${token}` } };

  const loadHistory = () => axios.get(`${API}/admin/settings/fuel-surcharge/history?months=12`, h).then((r) => setHistory(r.data || [])).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/admin/settings/fuel-surcharge`, h).then((r) => setCfg(r.data)).catch(() => {});
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lockMonth = async (month, price) => {
    try {
      await axios.put(`${API}/admin/settings/fuel-surcharge/history/${month}`, { current_price: price }, h);
      toast.success(`Prezzo gasolio ${month} salvato`);
      loadHistory();
    } catch { toast.error("Errore salvataggio mese"); }
  };
  const unlockMonth = async (month) => {
    try {
      await axios.delete(`${API}/admin/settings/fuel-surcharge/history/${month}`, h);
      toast.success(`${month}: torna al valore corrente`);
      loadHistory();
    } catch { toast.error("Errore"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/settings/fuel-surcharge`, cfg, h);
      setCfg(r.data);
      loadHistory();
      toast.success(`Supplemento carburante salvato: ${r.data.pct}%`);
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const num = (v) => (Number.isFinite(parseFloat(v)) ? parseFloat(v) : 0);
  const curPrice = num(cfg?.current_price);
  const basePrice = num(cfg?.base_price);
  const activeTier = (t) => cfg && curPrice >= t.from && curPrice < t.to;

  return (
    <section className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="section-fuel-surcharge">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-sm bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Fuel size={18} className="text-amber-700" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-zinc-800">Supplemento Carburante</h3>
          <p className="text-sm text-zinc-500 mt-1">
            Percentuale calcolata automaticamente dalla tabella a scaglioni in base al prezzo attuale del gasolio. Si applica in fattura sul totale trasporti (spedizioni + ritiri, escluso magazzino). Puoi disattivarlo per singolo cliente dalla sezione Fatturazione.
          </p>
        </div>
      </div>

      {!cfg ? (
        <p className="text-sm text-zinc-500"><Loader2 size={14} className="inline animate-spin mr-2" /> Caricamento...</p>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-3">
            <label className="flex items-center justify-between gap-4 p-3 border border-zinc-200 rounded-sm cursor-pointer">
              <span className="text-sm font-bold text-zinc-800">Applica supplemento carburante in fattura</span>
              <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="w-4 h-4 accent-[#FF5200]" data-testid="fuel-enabled" />
            </label>
            {FIELDS.map((f) => (
              <div key={f.key} className="flex items-center justify-between gap-4">
                <div>
                  <label className="text-sm text-zinc-700 font-medium block">{f.label}</label>
                  <span className="text-[11px] text-zinc-400">{f.hint}</span>
                </div>
                <input
                  type="number" step={f.step} min="0"
                  value={cfg[f.key] ?? ""}
                  onChange={(e) => setCfg({ ...cfg, [f.key]: e.target.value })}
                  className="w-32 h-9 px-3 border border-zinc-300 rounded-sm text-sm focus:border-[#FF5200] outline-none"
                  data-testid={`fuel-${f.key}`}
                />
              </div>
            ))}
            <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-sm">
              <span className="text-xs font-bold uppercase tracking-widest text-amber-800">Percentuale attuale</span>
              <span className="text-xl font-bold text-amber-800 font-mono" data-testid="fuel-current-pct">{cfg.pct}%</span>
            </div>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-[#e04800] disabled:opacity-50" data-testid="fuel-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Salva e ricalcola
            </button>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Tabella scaglioni</p>
            <div className="border border-zinc-200 rounded-sm overflow-hidden max-h-80 overflow-y-auto" data-testid="fuel-tiers-table">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500 sticky top-0">
                  <tr><th className="text-left px-3 py-2">Gasolio da (€/L)</th><th className="text-left px-3 py-2">a (€/L)</th><th className="text-right px-3 py-2">Supplemento</th></tr>
                </thead>
                <tbody>
                  <tr className={`border-t border-zinc-100 ${curPrice < basePrice ? "bg-amber-100 font-bold" : ""}`}>
                    <td className="px-3 py-1.5 font-mono">—</td><td className="px-3 py-1.5 font-mono">{basePrice.toFixed(3)}</td><td className="px-3 py-1.5 font-mono text-right">0%</td>
                  </tr>
                  {(cfg.tiers || []).map((t, i) => (
                    <tr key={i} className={`border-t border-zinc-100 ${activeTier(t) ? "bg-amber-100 font-bold" : ""}`} data-testid={activeTier(t) ? "fuel-tier-active" : undefined}>
                      <td className="px-3 py-1.5 font-mono">{t.from.toFixed(3)}</td>
                      <td className="px-3 py-1.5 font-mono">{t.to.toFixed(3)}</td>
                      <td className="px-3 py-1.5 font-mono text-right">+{t.pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-zinc-400 mt-2">Formula: % = (scaglione − prezzo base) ÷ prezzo base × incidenza. Le modifiche ai parametri aggiornano la tabella dopo il salvataggio.</p>
          </div>

          <div className="lg:col-span-2">
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1">Storico mensile prezzo gasolio</p>
            <p className="text-[11px] text-zinc-400 mb-2">Ogni salvataggio blocca il prezzo del mese corrente. I mesi bloccati <Lock size={10} className="inline" /> mantengono la loro % in fatture e dashboard anche se cambi i parametri; puoi correggere il prezzo di un mese passato e salvarlo.</p>
            <div className="border border-zinc-200 rounded-sm overflow-x-auto" data-testid="fuel-history-table">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
                  <tr><th className="text-left px-3 py-2">Mese</th><th className="text-left px-3 py-2">Gasolio (€/L)</th><th className="text-right px-3 py-2">Supplemento</th><th className="text-left px-3 py-2">Stato</th><th className="px-3 py-2"></th></tr>
                </thead>
                <tbody>
                  {history.map((r) => <HistoryRow key={r.month} r={r} onLock={lockMonth} onUnlock={unlockMonth} />)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function HistoryRow({ r, onLock, onUnlock }) {
  const [price, setPrice] = useState(String(r.current_price ?? ""));
  useEffect(() => { setPrice(String(r.current_price ?? "")); }, [r.current_price]);
  const changed = parseFloat(price) !== r.current_price;
  return (
    <tr className="border-t border-zinc-100" data-testid={`fuel-history-${r.month}`}>
      <td className="px-3 py-1.5 font-mono font-bold">{r.month}</td>
      <td className="px-3 py-1.5">
        <input type="number" step="0.001" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className="w-28 h-8 px-2 border border-zinc-300 rounded-sm text-sm font-mono focus:border-[#FF5200] outline-none" data-testid={`fuel-history-price-${r.month}`} />
      </td>
      <td className="px-3 py-1.5 font-mono text-right">{r.enabled ? `+${r.pct}%` : <span className="text-zinc-400">disattivo</span>}</td>
      <td className="px-3 py-1.5 text-xs">
        {r.frozen ? <span className="inline-flex items-center gap-1 text-emerald-700"><Lock size={11} /> bloccato</span> : <span className="inline-flex items-center gap-1 text-zinc-400"><Unlock size={11} /> valore corrente</span>}
      </td>
      <td className="px-3 py-1.5 text-right whitespace-nowrap">
        <button onClick={() => onLock(r.month, parseFloat(price))} disabled={!(parseFloat(price) > 0) || (!changed && r.frozen)} className="px-2 py-1 border border-zinc-300 text-[10px] font-bold uppercase tracking-wider hover:border-[#FF5200] hover:text-[#FF5200] disabled:opacity-40 rounded-sm" data-testid={`fuel-history-save-${r.month}`}>Salva</button>
        {r.frozen && <button onClick={() => onUnlock(r.month)} className="ml-1 px-2 py-1 border border-zinc-200 text-zinc-400 text-[10px] font-bold uppercase tracking-wider hover:border-red-400 hover:text-red-600 rounded-sm" data-testid={`fuel-history-unlock-${r.month}`}>Sblocca</button>}
      </td>
    </tr>
  );
}
